import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { AVIF_TYPE, WEBP_TYPE, dimsFor, srcSetFor } from "@/lib/image-formats";

const heroImage = { url: "/img/hero-sunset.webp" };
const heroMobileImage = { url: "/img/hero-mobile-sunset.webp" };
const heroDims = dimsFor(heroMobileImage.url);

export function Hero() {
  const [imageReady, setImageReady] = useState(false);
  const [fontsReady, setFontsReady] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (imgRef.current && imgRef.current.complete) {
      setImageReady(true);
    }
    if (typeof document !== "undefined" && document.fonts) {
      document.fonts.ready.then(() => setFontsReady(true));
    } else {
      setFontsReady(true);
    }
  }, []);

  const ready = imageReady && fontsReady;

  return (
    <section className="relative">
      {/* Hauteur réservée dès le premier paint : dvh avec repli vh, bornée pour les petits écrans */}
      <div className="relative h-[88vh] max-h-[760px] min-h-[520px] w-full overflow-hidden bg-sand sm:h-[92svh] sm:max-h-none sm:min-h-[560px]">
        {/* Une seule image téléchargée : format (AVIF/WebP) et largeur adaptés à l'écran */}
        <picture>
          <source
            media="(min-width: 640px)"
            type={AVIF_TYPE}
            srcSet={srcSetFor(heroImage.url, "avif")}
            sizes="100vw"
          />
          <source
            media="(min-width: 640px)"
            type={WEBP_TYPE}
            srcSet={srcSetFor(heroImage.url, "webp")}
            sizes="100vw"
          />
          <source
            type={AVIF_TYPE}
            srcSet={srcSetFor(heroMobileImage.url, "avif")}
            sizes="100vw"
          />
          <source
            type={WEBP_TYPE}
            srcSet={srcSetFor(heroMobileImage.url, "webp")}
            sizes="100vw"
          />
          <img
            ref={imgRef}
            src={heroMobileImage.url}
            alt="Panier en paille, chapeau de paille, lunettes de soleil et foulard sur une terrasse méditerranéenne au coucher du soleil"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            sizes="100vw"
            {...(heroDims ?? {})}
            onLoad={() => setImageReady(true)}
            className="absolute inset-0 h-full w-full object-cover object-[55%_38%] sm:object-center"
          />

        </picture>


        <div
          className="absolute inset-0 bg-gradient-to-b from-foreground/15 via-foreground/5 via-50% to-foreground/75 sm:from-foreground/25 sm:via-foreground/10 sm:via-50% sm:to-foreground/65"
          aria-hidden="true"
        />

        {/* Contenu du Hero : le texte réel est dans le flux pour réserver la hauteur exacte,
            le skeleton le recouvre jusqu'à la fin du chargement de l'image et des polices. */}
        <div className="absolute inset-x-0 bottom-0 mx-auto flex max-w-7xl flex-col items-center px-6 pb-12 text-onmedia sm:pb-20 lg:pb-24">
          {/* Texte réel : toujours présent dans le flux, invisible tant que le skeleton est actif */}
          <div
            className={cn(
              "flex w-full flex-col items-center text-center transition-opacity duration-700",
              ready ? "opacity-100" : "opacity-0",
            )}
          >
            <p className="label-xs text-[0.6rem] opacity-90">Sunset Concept Store</p>
            <h1 className="mt-4 font-serif text-[2.5rem] leading-[1.05] tracking-[0.01em] sm:mt-5 sm:text-6xl lg:text-7xl">
              L'été commence ici.
            </h1>
            <p className="mt-4 max-w-md text-sm leading-relaxed opacity-90 sm:mt-5 sm:text-base">
              Une sélection pensée pour vos plus beaux moments au soleil.
            </p>
            {/* Hauteur du CTA figée pour éviter tout décalage au chargement des polices */}
            <Link
              to="/nouveautes"
              className="btn-hero mt-7 w-full max-w-xs sm:mt-9 sm:w-auto"
            >
              Découvrir la collection
            </Link>
          </div>

          {/* Skeleton reproduisant l'emplacement exact du texte et du CTA */}
          <div
            className={cn(
              "absolute inset-0 flex flex-col items-center justify-start transition-opacity duration-700",
              ready ? "pointer-events-none opacity-0" : "opacity-100",
            )}
            aria-hidden="true"
          >
            {/* Eyebrow */}
            <div className="skeleton-shimmer h-3 w-36 rounded-sm" />

            {/* Title — une seule ligne sur tous les écrans */}
            <div className="skeleton-shimmer mt-4 h-10 w-2/3 rounded-sm sm:mt-5 sm:h-16 sm:w-1/2 lg:h-20 lg:w-2/5" />

            {/* Subtitle — 2 lignes sur tous les écrans */}
            <div className="mt-4 flex w-full max-w-md flex-col items-center gap-1 sm:mt-5">
              <div className="skeleton-shimmer h-5 w-full rounded-sm" />
              <div className="skeleton-shimmer h-5 w-3/4 rounded-sm" />
            </div>

            {/* CTA */}
            <div className="skeleton-shimmer mt-7 h-14 w-full max-w-xs rounded-sm sm:mt-9 sm:h-12 sm:w-72" />
          </div>
        </div>
      </div>
    </section>
  );
}

